import {EyeIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Flash, FormControl, TextInput} from '@primer/react'
import type {ReactElement, ReactNode, RefObject} from 'react'
import {useId, useRef, useState} from 'react'

import type {EditableShortcutField} from '../hooks/use-shortcut-editor'
import type {UserShortcut} from '../types'
import {isValidShortcutName, isValidShortcutQuery} from '../utils/shortcut'
import {IconAndColorPicker} from './IconAndColorPicker'
import styles from './ShortcutEditingHeader.module.css'

interface ShortcutEditingHeaderProps {
  /**
   * Render function that is expected to return a Filter component (or wrapper like ReposFilter).
   * The function receives an inputRef and className to pass to the Filter.
   *
   * Note: `'width-full'` is passed in automatically via the `className` prop
   * so that the Filter always takes the full width of its container.
   * This behavior can be overridden by passing a different `className` yourself.
   *
   * @example
   * ```tsx
   * renderFilter={filterProps => (
   *   <ReposFilter {...filterProps} {...props}/>
   * )}
   * ```
   */
  renderFilter: (filterProps: {inputRef: RefObject<HTMLInputElement | null>; className: string}) => ReactElement
  onDismiss: () => void
  onSave: () => void
  shortcut: UserShortcut
  setShortcutValue: (fieldName: EditableShortcutField, value: string) => void
  isSaving?: boolean
  errorMessage?: string
  helperText?: string
  /** Rendered at the end of the icon and title row, after the title field. */
  trailingField?: ReactNode
}

/**
 * Editing header component for creating or editing a saved view.
 *
 * All props can be passed in directly from the `useShortcutEditor` hook, apart from `renderFilter`, which is a
 * render prop for rendering a Filter component specific to the page in which this component is used.
 *
 * The filter component is intended to be shared between this component and the page's default filter UI,
 * to keep the query state consistent between the shortcut editor being open or closed.
 */
export function ShortcutEditingHeader({
  renderFilter,
  onDismiss,
  onSave,
  shortcut,
  setShortcutValue,
  isSaving = false,
  errorMessage,
  helperText,
  trailingField,
}: ShortcutEditingHeaderProps) {
  const titleRef = useRef<HTMLInputElement>(null)
  const filterRef = useRef<HTMLInputElement>(null)
  const helperTextId = useId()

  const [hasInteractedWithTitle, setHasInteractedWithTitle] = useState(false)

  function onSaveButtonClick() {
    if (!shortcut) throw new Error('Editing shortcut is undefined while submitting.')

    if (!isValidShortcutName(shortcut)) {
      setHasInteractedWithTitle(true)
      titleRef.current?.focus()
      return
    }

    if (!isValidShortcutQuery(shortcut)) {
      filterRef.current?.focus()
      return
    }

    onSave()
  }

  return (
    <form className="d-flex flex-column gap-2">
      {errorMessage && <Flash variant="danger">{errorMessage}</Flash>}
      <div className="d-flex flex-items-start gap-2">
        <FormControl>
          <FormControl.Label>Icon</FormControl.Label>
          <IconAndColorPicker
            selectedColor={shortcut.color}
            selectedIcon={shortcut.icon}
            onApply={(color, icon) => {
              setShortcutValue('color', color)
              setShortcutValue('icon', icon)
            }}
          />
        </FormControl>

        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <FormControl required className="grow width-full">
          <FormControl.Label>Title</FormControl.Label>
          <TextInput
            autoFocus
            ref={titleRef}
            className="width-full"
            placeholder="Title"
            value={shortcut.name}
            onChange={({target}) => {
              setHasInteractedWithTitle(true)
              setShortcutValue('name', target.value)
            }}
          />
          {!isValidShortcutName(shortcut) && hasInteractedWithTitle && (
            <FormControl.Validation variant="error">Title can not be empty</FormControl.Validation>
          )}
        </FormControl>

        {trailingField}
      </div>

      <FormControl>
        <FormControl.Label>Description</FormControl.Label>
        <TextInput
          className="width-full"
          placeholder="Add a description"
          value={shortcut.description}
          onChange={({target}) => setShortcutValue('description', target.value)}
        />
      </FormControl>

      <div className={styles.FilterAndButtonsContainer}>
        <FormControl required className={styles.FilterFormControl}>
          <FormControl.Label>Query</FormControl.Label>
          {renderFilter({inputRef: filterRef, className: 'width-full'})}
          {!isValidShortcutQuery(shortcut) && (
            <FormControl.Validation variant="error">Query can not be empty</FormControl.Validation>
          )}
        </FormControl>
        <div className={styles.ActionButtons}>
          <Button type="button" onClick={onDismiss} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={onSaveButtonClick}
            disabled={isSaving}
            aria-describedby={helperText ? helperTextId : undefined}
          >
            {isSaving ? 'Saving...' : 'Save view'}
          </Button>
        </div>
      </div>

      {helperText && (
        <p id={helperTextId} className={styles.HelperText}>
          <EyeIcon size={16} className={styles.HelperTextIcon} />
          {helperText}
        </p>
      )}
    </form>
  )
}
